import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, StatCard } from "../components/shared/Primitives";
import { Button } from "../components/ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
} from "recharts";

const COLORS = ["#0F1115", "#0090FF", "#46A758", "#FFC53D", "#F76B15", "#E54D2E", "#8E4EC6"];

export default function Dashboard() {
  const [data, setData] = useState(null);

  const load = async () => {
    const { data } = await api.get("/dashboard");
    setData(data);
  };
  useEffect(() => {
    load();
  }, []);

  const pipeData = data
    ? Object.entries(data.pipeline_by_month).sort().slice(-6).map(([m, v]) => ({ month: m, amount: v }))
    : [];
  const catData = data
    ? Object.entries(data.expenses_by_category).map(([k, v]) => ({ name: k, value: v }))
    : [];

  return (
    <div>
      <PageHeader
        eyebrow="Overview"
        title="Dashboard"
        description="A cross-section of every operation in your CRM today."
        right={
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const res = await api.get("/export/all", { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                const cd = res.headers["content-disposition"] || "";
                const m = cd.match(/filename="?([^"]+)"?/);
                a.download = m ? m[1] : `ristwatch-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast.success("Export downloaded");
              } catch (e) {
                toast.error("Export failed");
              }
            }}
            data-testid="download-all-btn"
          >
            <Download size={14} className="mr-2" /> Download all data
          </Button>
        }
      />
      <div className="p-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Contacts" value={data?.contacts_count ?? "—"} testid="stat-contacts" />
          <StatCard label="Today's Tasks" value={data?.daily_today ?? "—"} accent="var(--rw-dot-todo)" testid="stat-today" />
          <StatCard
            label="Pipeline (this month)"
            value={data ? `$${Number(data.pipeline_total).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
            hint={`${data?.closed_count ?? 0} closed`}
            accent="var(--rw-dot-pipeline)"
            testid="stat-pipeline"
          />
          <StatCard label="Calls (this month)" value={data?.calls_month ?? "—"} accent="var(--rw-dot-calling)" testid="stat-calls" />
          <StatCard label="Intros (this month)" value={data?.intros_month ?? "—"} accent="var(--rw-dot-intros)" testid="stat-intros" />
          <StatCard label="Networking events" value={data?.events_count ?? "—"} testid="stat-events" />
          <StatCard
            label="Spend (this month)"
            value={data ? `$${Number(data.expenses_total).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
            testid="stat-spend"
          />
          <StatCard
            label="Follow-ups overdue"
            value={data?.follow_ups_overdue ?? "—"}
            hint={`${data?.follow_ups_today ?? 0} due today`}
            testid="stat-overdue"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-[var(--rw-border)] rounded-[4px] p-5">
            <div className="label-eyebrow mb-3">Pipeline · trailing 6 months</div>
            <div className="h-60">
              {pipeData.length === 0 ? (
                <div className="h-full grid place-items-center text-sm text-neutral-400">
                  No pipeline activity yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pipeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v / 1000}k`} />
                    <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <Bar dataKey="amount" fill="#0F1115" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-white border border-[var(--rw-border)] rounded-[4px] p-5">
            <div className="label-eyebrow mb-3">Spend · by category (all time)</div>
            <div className="h-60">
              {catData.length === 0 ? (
                <div className="h-full grid place-items-center text-sm text-neutral-400">
                  No expenses yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={catData} dataKey="value" outerRadius={80} innerRadius={45} paddingAngle={1}>
                      {catData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
          {[
            { to: "/operations/daily", label: "Daily To-Do", color: "var(--rw-dot-todo)" },
            { to: "/operations/pipeline", label: "Pipeline", color: "var(--rw-dot-pipeline)" },
            { to: "/operations/calling", label: "Calling", color: "var(--rw-dot-calling)" },
            { to: "/operations/intros", label: "Intros", color: "var(--rw-dot-intros)" },
            { to: "/operations/schedule", label: "Schedule", color: "var(--rw-dot-schedule)" },
          ].map((q) => (
            <Link
              key={q.to}
              to={q.to}
              className="bg-white border border-[var(--rw-border)] rounded-[4px] p-4 hover:bg-[var(--rw-surface-2)] transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="nav-dot" style={{ background: q.color }} />
                <span className="label-eyebrow">Open</span>
              </div>
              <div className="font-display text-lg font-semibold mt-1">{q.label}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
